import { MutableRefObject, useEffect, useRef } from 'react'

import { Skeleton } from 'antd'
import styled from 'styled-components'

import { Text } from '../shared-components/BUI/Text'
import PreventPageBreak from '../shared-components/PrintMedia'

import { CorePageListViewEmpty } from './CorePageListViewEmpty'
import { CorePageListViewFilterBar } from './CorePageListViewFilterBar'
import {
  CorePageListViewHeader,
  MultiSelectActions,
} from './CorePageListViewHeader'
import { CorePageListViewPagination } from './CorePageListViewPagination'
import { SortOption } from './CorePageListViewSort'
import {
  CorePageListViewViewsFilterBar,
  View,
} from './CorePageListViewViewsFilterBar'
import { CorePageLoading } from './CorePageLoading'
import { CorePageTitle } from './CorePageTitle'

import {
  CheckableActionsRef,
  ListViewProvider,
} from '@/context/listViewContext'
import { ListViewColumnConfig } from '@/hooks/useColumnConfig'
import usePreserveScroll from '@/hooks/usePreserveScroll'
import {
  CustomPaginationQueryKeys,
  DEFAULT_SEARCH_FILTER_KEY,
} from '@/utils/params'
import { pluraliseWord } from '@/utils/pluralise'

import { PaginationAggregation } from 'types/api.graphql'

const ListViewWrapper = styled.div`
  margin-bottom: 4rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const ListViewHeader = styled.div`
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem 0.5rem;

  ${({ theme: { media } }) => media.lg`
    flex-direction: row-reverse;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
  `}
`

const ListViewActions = styled.div`
  ${({ theme: { media } }) => media.md`
    align-self: flex-end;
    margin-left: auto; // for when there is no title
  `}
`

type WrapperProps = {
  square?: boolean
}

// DO NOT ADD FLEX TO THIS IT WILL RUIN PDFS
const ListDataWrapper = styled.div<WrapperProps>`
  border-radius: ${({ square }) => (square ? '0px' : '6px')};
  overflow: hidden;
`

const LoadingWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
  ${({ theme }) => theme.font.caption01}
  padding: 2rem 1rem;
  text-align: center;
`

export const GroupRow = styled(PreventPageBreak).attrs({
  shouldAvoidPageBreak: true,
})`
  ${({ theme }) => theme.font.caption02}
  padding: 0.5rem 1rem;
  background-color: ${({ theme }) => theme.colour.secondary._50};
  color: ${({ theme }) => theme.colour.secondary.base};
  border-bottom: 1px solid ${({ theme }) => theme.colour.neutral.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const FakeRow = styled.div`
  background-color: ${({ theme }) => theme.colour.neutral.white};
  border-bottom: 1px solid ${({ theme }) => theme.colour.neutral.border};
  padding: 1.5rem 1rem;
`

type Props = {
  resourceSingular: string
  loading: boolean
  actionMenu?: React.ReactNode
  searchable?: boolean
  searchFilterKey?: string
  filters?: Array<React.ReactNode | React.ReactNode[]>
  views?: View[]
  preFilterContent?: React.ReactNode
  preListContent?: React.ReactNode
  aggregation?: Omit<PaginationAggregation, '__typename'>
  square?: boolean
  showResultCount?: boolean
  emptyMessage?: React.ReactNode
  multiSelectActions?: MultiSelectActions
  checkableActionsRef?: MutableRefObject<CheckableActionsRef>
  checkable?: () => Promise<string[]>
  sortOptions?: SortOption[]
  titleOverride?: React.ReactNode
  titleExtra?: React.ReactNode
  showTitle?: boolean
  footer?: React.ReactNode
  children?: React.ReactNode
  subtitle?: React.ReactNode
  useQuery?: boolean
  onSelectionChange?: (ids: string[]) => void
  isPublic?: boolean
  selectAllFunction?: () => Promise<string[]>
  showPagination?: boolean
  customPaginationQueryKeys?: CustomPaginationQueryKeys
  columnConfig?: ListViewColumnConfig
  clearSelectionOnProjectChange?: boolean
  style?: React.CSSProperties
  pageSizeOptions?: number[]
  hideBreadcrumbs?: boolean
}

export const CorePageListView: React.FC<Props> = ({
  resourceSingular,
  loading,
  actionMenu,
  searchable,
  searchFilterKey = DEFAULT_SEARCH_FILTER_KEY,
  filters,
  views,
  preFilterContent,
  preListContent,
  aggregation,
  square,
  showResultCount = true,
  emptyMessage,
  checkable,
  sortOptions,
  multiSelectActions,
  checkableActionsRef,
  titleOverride,
  titleExtra,
  subtitle,
  showTitle = true,
  footer,
  useQuery,
  onSelectionChange,
  isPublic = false,
  children,
  selectAllFunction,
  showPagination = true,
  customPaginationQueryKeys,
  columnConfig,
  clearSelectionOnProjectChange,
  style,
  pageSizeOptions,
  hideBreadcrumbs,
}) => {
  usePreserveScroll()

  const isFirstRender = useRef(true)

  const resourcePlural = pluraliseWord(2, resourceSingular?.toString())

  const showEmpty = !loading && aggregation?.totalCount === 0

  const showHeader =
    (!!aggregation || !!checkable) && (showResultCount || !!multiSelectActions)

  const showFilterBar = !!searchable || !!filters?.length || !!views?.length

  useEffect(() => {
    if (isFirstRender.current && !!children) {
      isFirstRender.current = false
    }
  }, [children])

  if (loading && isFirstRender.current) {
    return (
      <ListViewWrapper>
        <ListViewHeader>
          {showTitle && <Skeleton.Input active size="small" />}

          <Skeleton.Button active />
        </ListViewHeader>

        {showFilterBar && (
          <>
            {views?.length ? (
              <CorePageListViewViewsFilterBar views={views} loading />
            ) : (
              <CorePageListViewFilterBar loading />
            )}
          </>
        )}

        <ListDataWrapper square={square}>
          <CorePageListViewHeader
            loading
            isPublic={isPublic}
            hideBreadcrumbs={hideBreadcrumbs}
          />
          <FakeRow>
            <Skeleton active paragraph={{ rows: 2 }} />
          </FakeRow>
          <FakeRow>
            <Skeleton active paragraph={{ rows: 2 }} />
          </FakeRow>
        </ListDataWrapper>
      </ListViewWrapper>
    )
  }

  return (
    <ListViewProvider
      resourceSingular={resourceSingular?.toString()}
      aggregation={aggregation}
      checkable={checkable}
      checkableActionsRef={checkableActionsRef}
      onSelectionChange={onSelectionChange}
      selectAllFunction={selectAllFunction}
      clearSelectionOnProjectChange={clearSelectionOnProjectChange}
    >
      <ListViewWrapper style={style}>
        {(actionMenu || showTitle) && (
          <ListViewHeader>
            {actionMenu && <ListViewActions>{actionMenu}</ListViewActions>}
            {showTitle && (
              <CorePageTitle
                titleOverride={titleOverride}
                titleExtra={titleExtra}
              />
            )}
          </ListViewHeader>
        )}

        {subtitle && <Text secondary="_500">{subtitle}</Text>}

        {preFilterContent}

        {showFilterBar && (
          <>
            {views?.length ? (
              <CorePageListViewViewsFilterBar
                views={views}
                filters={filters}
                searchable={searchable}
                searchFilterKey={searchFilterKey}
                sortOptions={sortOptions}
                useQuery={useQuery}
              />
            ) : (
              <CorePageListViewFilterBar
                filters={filters}
                searchable={searchable}
                searchFilterKey={searchFilterKey}
                sortOptions={sortOptions}
                useQuery={useQuery}
              />
            )}
          </>
        )}

        {preListContent}

        <ListDataWrapper square={square}>
          {showHeader && (
            <CorePageListViewHeader
              isPublic={isPublic}
              showResultCount={showResultCount}
              multiSelectActions={multiSelectActions}
              columnConfig={columnConfig}
              hideBreadcrumbs={hideBreadcrumbs}
            />
          )}

          {loading ? (
            <LoadingWrapper>
              <CorePageLoading text={`Loading ${resourcePlural}`} />
            </LoadingWrapper>
          ) : (
            children
          )}

          {showEmpty &&
            (emptyMessage || <CorePageListViewEmpty useQuery={useQuery} />)}
        </ListDataWrapper>

        {footer}

        {showPagination && (
          <CorePageListViewPagination
            useQuery={useQuery}
            customQueryKeys={customPaginationQueryKeys}
            pageSizeOptions={pageSizeOptions}
          />
        )}
      </ListViewWrapper>
    </ListViewProvider>
  )
}
